import { useMemo } from 'react'
import clsx from 'clsx'
import { HugeiconsIcon, HugeiconsIconProps } from '@hugeicons/react'
import { Tick01Icon } from '@hugeicons/core-solid-rounded'
import { ArrowRightDoubleIcon } from '@hugeicons/core-stroke-rounded'

export type ValueType = string | number

export type Step = {
  value: ValueType
  label: string
  icon: HugeiconsIconProps['icon']
}

type Props = {
  value: ValueType
  steps: Step[]
  onChange?: (value: ValueType) => void
}

export function ProgressStepper({ value, steps, onChange }: Props) {
  const activeIndex = useMemo(() => {
    return steps.findIndex(step => step.value === value)
  }, [value, steps])

  const lastIndex = useMemo(() => steps.length - 1, [steps])

  return (
    <ol className="lg:flex items-center justify-center w-full space-y-4 lg:space-y-0  ">
      {steps.map(function (step, index) {
        const isDone = index < activeIndex
        const isActive = index === activeIndex
        const isDoneOrActive = isDone || isActive
        const isLast = lastIndex === index

        return (
          <li key={step.value} className="flex flex-row items-center">
            <button
              tabIndex={onChange ? 0 : -1}
              onClick={() => onChange?.(step.value)}
              className={clsx(
                'flex items-center font-medium p-3 w-full rounded-small cursor-pointer',
                !onChange && 'pointer-events-none'
              )}
            >
              <span
                className={clsx(
                  'size-8 rounded-small flex justify-center items-center mr-3 lg:size-10 border border-secondary p-2',
                  isDoneOrActive && 'bg-secondary text-white',
                  !isDoneOrActive && 'text-secondary bg-secondary/5'
                )}
              >
                <HugeiconsIcon
                  icon={isDone ? Tick01Icon : step.icon}
                  className="size-full"
                />
              </span>
              <h4
                className={clsx(
                  'text-regular',
                  isActive && 'text-secondary',
                  !isActive && 'text-text'
                )}
              >
                {step.label}
              </h4>
            </button>
            {!isLast && (
              <div>
                <HugeiconsIcon
                  icon={ArrowRightDoubleIcon}
                  className="size-6 text-text-light"
                />
              </div>
            )}
          </li>
        )
      })}
    </ol>
  )
}
